import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { WaveAnnouncement as WaveAnnouncementType, WaveType } from '../game/waves/WaveManager';

// Animation keyframes
const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(-20px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const fadeOut = keyframes`
  0% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(20px); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

// Styled components
const AnnouncementContainer = styled.div<{ $visible: boolean; $isExiting: boolean }>`
  position: fixed;
  top: 20%;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 8px;
  padding: 20px 40px;
  color: white;
  text-align: center;
  z-index: 1000;
  animation: ${props => (props.$isExiting ? fadeOut : fadeIn)} 0.5s ease-in-out forwards;
  display: ${props => (props.$visible ? 'block' : 'none')};
  box-shadow: 0 0 20px rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
`;

const WaveNumber = styled.h1`
  font-size: 2.5rem;
  margin: 0 0 10px 0;
  font-weight: bold;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
  animation: ${pulse} 2s infinite;
`;

const WaveMessage = styled.p`
  font-size: 1.5rem;
  margin: 0;
  opacity: 0.9;
`;

const WaveTypeIndicator = styled.div<{ $waveType: WaveType }>`
  display: inline-block;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: bold;
  margin-bottom: 10px;
  text-transform: uppercase;
  background-color: ${props => {
    switch (props.$waveType) {
      case WaveType.BOSS:
        return 'rgba(255, 0, 0, 0.7)';
      case WaveType.HORDE:
        return 'rgba(255, 165, 0, 0.7)';
      case WaveType.ELITE:
        return 'rgba(128, 0, 128, 0.7)';
      case WaveType.SPECIAL:
        return 'rgba(0, 191, 255, 0.7)';
      default:
        return 'rgba(0, 128, 0, 0.7)';
    }
  }};
  color: white;
`;

interface WaveAnnouncementProps {
  announcement: WaveAnnouncementType | null;
}

/**
 * Component for displaying wave announcements
 */
const WaveAnnouncement: React.FC<WaveAnnouncementProps> = ({ announcement }) => {
  const [visible, setVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [currentAnnouncement, setCurrentAnnouncement] = useState<WaveAnnouncementType | null>(null);
  
  useEffect(() => {
    if (announcement) {
      // If we're already showing an announcement, hide it first
      if (visible) {
        setIsExiting(true);
        
        // After exit animation, show the new announcement
        setTimeout(() => {
          setIsExiting(false);
          setCurrentAnnouncement(announcement);
          setVisible(true);
          
          // Hide after the specified duration
          setTimeout(() => {
            setIsExiting(true);
            
            // After exit animation, hide completely
            setTimeout(() => {
              setVisible(false);
              setIsExiting(false);
            }, 500);
          }, announcement.duration * 1000);
        }, 500);
      } else {
        // Show the announcement immediately
        setCurrentAnnouncement(announcement);
        setVisible(true);
        
        // Hide after the specified duration
        setTimeout(() => {
          setIsExiting(true);
          
          // After exit animation, hide completely
          setTimeout(() => {
            setVisible(false);
            setIsExiting(false);
          }, 500);
        }, announcement.duration * 1000);
      }
    }
  }, [announcement]);
  
  if (!currentAnnouncement) return null;
  
  return (
    <AnnouncementContainer $visible={visible} $isExiting={isExiting}>
      <WaveTypeIndicator $waveType={currentAnnouncement.waveType}>
        {currentAnnouncement.waveType} Wave
      </WaveTypeIndicator>
      <WaveNumber>Wave {currentAnnouncement.waveNumber}</WaveNumber>
      <WaveMessage>{currentAnnouncement.message}</WaveMessage>
    </AnnouncementContainer>
  );
};

export default WaveAnnouncement; 